import React, { lazy } from 'react';
import { Outlet, Route } from 'react-router';
import InvitationView from '../account/InvitationView';
import ExtensionLoginView from '../account/login/ExtensionLoginView';
import LoginView from '../account/login/LoginView';
import McpAuthorizeView from '../account/login/McpAuthorizeView';
import RegisterWithProvisionView from '../account/login/RegisterWithProvisionView';
import ForgotView from '../account/password/ForgotView';
import MagicLinkView from '../account/password/MagicLinkView';
import ResetView from '../account/password/ResetView';
import AdminAppearanceView from '../admin/appearance/AdminAppearanceView';
import AuditView from '../admin/audit/AuditView';
import AdminBetaView from '../admin/beta/AdminBetaView';
import AdminBillingView from '../admin/billing/AdminBillingView';
import AdminDeveloperView from '../admin/developer/AdminDeveloperView';
import AdminWebhookEditView from '../admin/developer/AdminWebhookEditView';
import AdminWebhookNewView from '../admin/developer/AdminWebhookNewView';
import AdminImportView from '../admin/import/AdminImportExportView';
import AdminIntegrationView from '../admin/integration/AdminIntegrationView';
import AdminProvisionEditView from '../admin/provisions/AdminProvisionEditView';
import AdminProvisionNewView from '../admin/provisions/AdminProvisionNewView';
import AdminProvisionsView from '../admin/provisions/AdminProvisionsView';
import AdminSecurityView from '../admin/security/AdminSecurityView';
import TeammatesView from '../admin/teammates/TeammatesView';
import AppLaunchView from '../desktop/AppLaunchView';
import NotFoundView from '../error/NotFoundView';
import GroupsView from '../groups/GroupsView';
import GroupView from '../groups/GroupView';
import HelpMenu from '../help/HelpMenu';
import DeletedView from '../home/DeletedView';
import DraftsView from '../home/DraftsView';
import HomeView from '../home/HomeView';
import CreatePostView from '../post/CreatePostView';
import EmbeddedPostView from '../post/EmbeddedPostView';
import EmbedLoginView from '../post/EmbedLoginView';
import ExternalPostsView from '../post/ExternalPostsView';
import {
  HistoricalThreadRedirect,
  PostHistoryRedirect,
} from '../post/history/PostHistoryView';
import PostVerifyView from '../post/PostVerifyView';
import PostView from '../post/PostView';
import PublicPostView from '../post/PublicPostView';
import TokenRefreshPage from '../post/TokenRefreshPage';
import PublicApp from '../public/PublicApp';
import SearchView from '../search/SearchView';
import Sidebar from '../sidebar/Sidebar';
import TemplatesView from '../templates/TemplatesView';
import ExternalTopbar from '../topbar/ExternalTopbar';
import InternalTopbar from '../topbar/InternalTopbar';
import AllTopicsView from '../topic/AllTopicsView';
import PublicTopicView from '../topic/PublicTopicView';
import TopicSubtopicsView from '../topic/TopicSubtopicsView';
import TopicView from '../topic/TopicView';
import UnorganizedView from '../topic/UnorganizedView';
import ExtensionApp from './extension-ui/ExtensionApp';
import ExtensionPost from './extension-ui/ExtensionPost';
import ExtensionPostList from './extension-ui/ExtensionPostList';
import AppRoot from './layout/AppRoot';
import InternalApp from './layout/InternalApp';
import Page from './layout/Page';
import Redirect from './router/Redirect';
import RootRouteErrorBoundary from './router/RootRouteErrorBoundary';
import UserRoleRouter from './router/UserRoleRouter';
import UserProfileView from '../user/profile/UserProfileView';
import ConnectExternalIdentity, {
  ConnectIdentityStatus,
} from '../user/settings/ConnectExternalIdentity';
import UserAccountSettings from '../user/settings/UserAccountSettings';
import UserNotificationSettings from '../user/settings/UserNotificationSettings';
import UserProfileSettingsView from '../user/settings/UserProfileSettingsView';

const TeamInsightsView = lazy(
  () =>
    import(
      /* webpackChunkName: "insights" */
      '../admin/insights/TeamInsightsView'
    ),
);

const SearchInsightsView = lazy(
  () =>
    import(
      /* webpackChunkName: "search-insights" */
      '../admin/insights/search/SearchInsightsView'
    ),
);

const appRoutes = (
  <Route
    path="/"
    element={<AppRoot />}
    errorElement={<RootRouteErrorBoundary />}
  >
    <Route path="public" element={<PublicApp />}>
      <Route
        handle={{ routeName: 'post' }}
        path="posts/:postSlug"
        element={
          <Page topbar={<ExternalTopbar />}>
            <PublicPostView />
          </Page>
        }
      />
      <Route
        handle={{ routeName: 'topic' }}
        path="topics/:topicSlug"
        element={
          <Page topbar={<ExternalTopbar />}>
            <PublicTopicView />
          </Page>
        }
      />
      <Route
        handle={{ routeName: '404' }}
        path="*"
        element={
          <Page topbar={<ExternalTopbar />}>
            <NotFoundView />
          </Page>
        }
      />
    </Route>

    <Route
      element={<InternalApp />}
      handle={{
        ensureRole: 'user',
        pageLayout: { sidebar: <Sidebar /> },
      }}
    >
      <Route
        index
        handle={{ routeName: 'home' }}
        element={
          <Page topbar={<InternalTopbar />}>
            <HomeView />
          </Page>
        }
      />

      <Route
        handle={{ routeName: 'drafts' }}
        path="drafts"
        element={
          <Page topbar={<InternalTopbar />}>
            <DraftsView />
          </Page>
        }
      />
      <Route
        handle={{ routeName: 'drafts' }}
        path="drafts/new"
        element={
          <Page topbar={<InternalTopbar />}>
            <CreatePostView />
          </Page>
        }
      />

      <Route
        path="templates/:templateSlug"
        element={<Redirect to="/posts/:templateSlug" />}
      />

      <Route
        handle={{ routeName: 'templates' }}
        path="templates"
        element={
          <Page topbar={<InternalTopbar />}>
            <TemplatesView />
          </Page>
        }
      />

      <Route
        handle={{ routeName: 'pricing' }}
        path="pricing"
        element={
          <Page topbar={<InternalTopbar />}>
            <HomeView openModal="pricing" />
          </Page>
        }
      />

      <Route
        handle={{ routeName: 'refer' }}
        path="refer"
        element={
          <Page topbar={<InternalTopbar />}>
            <HomeView openModal="referral" />
          </Page>
        }
      />

      <Route
        path="drafts/:postSlug"
        element={<Redirect to="/posts/:postSlug" />}
      />

      <Route path="posts/:postSlug">
        <Route index handle={{ routeName: 'post' }} element={<PostView />} />
        <Route
          path="verify"
          handle={{ routeName: 'post-verify' }}
          element={<PostVerifyView />}
        />
        <Route
          handle={{ routeName: 'comment-history' }}
          path="comments"
          element={
            <Page topbar={<InternalTopbar />}>
              <HistoricalThreadRedirect />
            </Page>
          }
        />
        <Route
          handle={{ routeName: 'post-history' }}
          path="histories"
          element={
            <Page topbar={<InternalTopbar />}>
              <PostHistoryRedirect />
            </Page>
          }
        />
        <Route
          handle={{ routeName: 'post-history' }}
          path="histories/:versionFrom/:versionTo"
          element={
            <Page topbar={<InternalTopbar />}>
              <PostHistoryRedirect />
            </Page>
          }
        />
      </Route>

      <Route
        handle={{ routeName: 'search' }}
        path="search"
        element={
          <Page topbar={<InternalTopbar />}>
            <SearchView />
          </Page>
        }
      />

      <Route path="topics">
        <Route
          index
          handle={{ routeName: 'all-topics' }}
          element={
            <Page topbar={<InternalTopbar />}>
              <AllTopicsView />
            </Page>
          }
        />
        <Route path=":topicSlug">
          <Route
            index
            handle={{ routeName: 'topic' }}
            element={
              <Page topbar={<InternalTopbar />}>
                <TopicView />
              </Page>
            }
          />
          <Route
            handle={{ routeName: 'topic' }}
            path="subtopics"
            element={
              <Page topbar={<InternalTopbar />}>
                <TopicSubtopicsView />
              </Page>
            }
          />
          <Route path="*" element={<Redirect to="/topics/:topicSlug" />} />
        </Route>
      </Route>

      <Route path="integration-posts">
        <Route
          handle={{ routeName: 'integration-post' }}
          path=":platform"
          element={
            <Page topbar={<InternalTopbar />}>
              <ExternalPostsView />
            </Page>
          }
        />
      </Route>

      <Route
        path="/external-posts/:platform"
        element={<Redirect to="/integration-posts/:platform" />}
      />

      <Route
        handle={{ routeName: 'unorganized' }}
        path="unorganized"
        element={
          <Page topbar={<InternalTopbar />}>
            <UnorganizedView />
          </Page>
        }
      />

      <Route
        handle={{ routeName: 'user' }}
        path="users/:userSlug"
        element={
          <Page topbar={<InternalTopbar />}>
            <UserProfileView />
          </Page>
        }
      />

      <Route path="groups">
        <Route
          index
          handle={{ routeName: 'groups' }}
          element={
            <Page topbar={<InternalTopbar />}>
              <GroupsView />
            </Page>
          }
        />
        <Route
          handle={{ routeName: 'group' }}
          path=":groupSlug"
          element={
            <Page topbar={<InternalTopbar />}>
              <GroupView />
            </Page>
          }
        />
      </Route>
    </Route>

    <Route
      element={<InternalApp />}
      handle={{
        ensureRole: 'admin',
        pageLayout: { sidebar: <Sidebar /> },
      }}
    >
      <Route
        handle={{ routeName: 'insights' }}
        path="admin/insights"
        element={
          <Page topbar={<InternalTopbar />}>
            <TeamInsightsView />
          </Page>
        }
      />
      <Route
        handle={{ routeName: 'search-insights' }}
        path="admin/insights/search"
        element={
          <Page topbar={<InternalTopbar />}>
            <SearchInsightsView />
          </Page>
        }
      />
    </Route>

    <Route element={<InternalApp />} handle={{ ensureRole: 'user' }}>
      <Route handle={{ routeName: 'settings' }} path="settings">
        <Route index element={<UserAccountSettings />} />
        <Route
          handle={{ routeName: 'settings' }}
          path="profile"
          element={<UserProfileSettingsView />}
        />
        <Route
          handle={{ routeName: 'settings' }}
          path="notifications"
          element={<UserNotificationSettings />}
        />
        <Route
          handle={{ routeName: 'identity-connect' }}
          path="identity/connect/finish"
          element={<ConnectIdentityStatus />}
        />
        <Route
          handle={{ routeName: 'identity-connect' }}
          path="identity/connect/:platform"
          element={<ConnectExternalIdentity />}
        />
      </Route>

      <Route path="teammates" element={<Redirect to="/admin/teammates" />} />

      <Route
        handle={{ routeName: 'app-launch' }}
        path="desktop/login"
        element={<AppLaunchView />}
      />

      <Route
        handle={{ routeName: 'extension-login' }}
        path="extension/login"
        element={<ExtensionLoginView />}
      />

      <Route
        path="embed/login"
        handle={{ routeName: 'embed-login' }}
        element={<EmbedLoginView />}
      />

      <Route
        path="mcp/authorize"
        handle={{ routeName: 'mcp-authorize' }}
        element={<McpAuthorizeView />}
      />
    </Route>

    <Route element={<InternalApp />} handle={{ ensureRole: 'admin' }}>
      <Route handle={{ routeName: 'admin' }} path="admin">
        <Route index element={<Redirect to="/admin/appearance" />} />
        <Route
          handle={{ routeName: 'admin' }}
          path="teammates"
          element={<TeammatesView />}
        />
        <Route
          handle={{ routeName: 'admin' }}
          path="billing"
          element={<AdminBillingView />}
        />
        <Route
          handle={{ routeName: 'admin' }}
          path="provisions"
          element={<AdminProvisionsView />}
        />
        <Route
          handle={{ routeName: 'admin' }}
          path="provisions/new"
          element={<AdminProvisionNewView />}
        />
        <Route
          handle={{ routeName: 'admin' }}
          path="provisions/:id"
          element={<AdminProvisionEditView />}
        />
        <Route path="import">
          <Route
            index
            handle={{ routeName: 'admin' }}
            element={<AdminImportView />}
          />
          <Route
            handle={{ routeName: 'admin' }}
            path=":platform"
            element={<AdminImportView />}
          />
          <Route
            handle={{ routeName: 'admin' }}
            path=":platform/:status"
            element={<AdminImportView />}
          />
        </Route>
        <Route path="integrations">
          <Route
            index
            handle={{ routeName: 'admin' }}
            element={<AdminIntegrationView />}
          />
          <Route
            handle={{ routeName: 'admin' }}
            path=":platform"
            element={<AdminIntegrationView />}
          />
          <Route
            handle={{ routeName: 'admin' }}
            path=":platform/:status"
            element={<AdminIntegrationView />}
          />
        </Route>
        <Route
          handle={{ routeName: 'admin' }}
          path="security"
          element={<AdminSecurityView />}
        />
        <Route handle={{ routeName: 'admin' }} path="developer">
          <Route index element={<AdminDeveloperView />} />
          <Route
            handle={{ routeName: 'admin' }}
            path="webhooks/new"
            element={<AdminWebhookNewView />}
          />
          <Route
            handle={{ routeName: 'admin' }}
            path="webhooks/:id"
            element={<AdminWebhookEditView />}
          />
        </Route>
        <Route
          handle={{ routeName: 'admin' }}
          path="appearance"
          element={<AdminAppearanceView />}
        />
        <Route
          handle={{ routeName: 'admin' }}
          path="audit"
          element={<AuditView />}
        />
        <Route
          handle={{ routeName: 'admin' }}
          path="beta"
          element={<AdminBetaView />}
        />
      </Route>
    </Route>

    <Route element={<InternalApp />} handle={{ ensureRole: 'anonymous' }}>
      <Route
        element={
          <Page>
            <Outlet />
            <HelpMenu appearance="public" />
          </Page>
        }
      >
        <Route
          path="forgot"
          handle={{ routeName: 'forgot' }}
          element={<ForgotView />}
        />
        <Route
          path="login"
          handle={{ routeName: 'login' }}
          element={<LoginView />}
        />
        <Route
          path="invitations/:id"
          handle={{ routeName: 'invitation' }}
          element={<InvitationView />}
        />
        <Route
          path="magic-link/:token"
          handle={{ routeName: 'reset' }}
          element={<MagicLinkView />}
        />
        <Route
          path="register/:provisionId/:provisionSecret"
          handle={{ routeName: 'register' }}
          element={<RegisterWithProvisionView />}
        />
        <Route
          path="resets/:token"
          handle={{ routeName: 'reset' }}
          element={<ResetView />}
        />
      </Route>
    </Route>

    <Route element={<InternalApp />} handle={{ ensureRole: 'userOrAnonymous' }}>
      <Route
        handle={{ routeName: 'embed' }}
        path="embed/:postSlug"
        element={<EmbeddedPostView />}
      />
    </Route>

    <Route element={<InternalApp />}>
      <Route
        path="deleted"
        handle={{ routeName: 'deleted' }}
        element={
          <Page>
            <DeletedView />
          </Page>
        }
      />
    </Route>
    <Route
      handle={{ ensureRole: 'userOrAnonymous' }}
      element={
        <UserRoleRouter
          user={<InternalApp />}
          anonymous={<Redirect to="/public/posts/:*" />}
        />
      }
    >
      <Route
        path="*"
        handle={{
          routeName: '404',
          pageLayout: { sidebar: <Sidebar /> },
        }}
        element={
          <Page topbar={<InternalTopbar />}>
            <NotFoundView />
          </Page>
        }
      />
    </Route>

    <Route
      path="tokens/refresh"
      handle={{ ensureRole: 'userOrAnonymous' }}
      element={<TokenRefreshPage />}
    />

    <Route path="extension-ui" element={<ExtensionApp />}>
      <Route path="post" element={<ExtensionPost />} />
      <Route path="post-list" element={<ExtensionPostList />} />
    </Route>
  </Route>
);

export default appRoutes;
